import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useUser } from "@clerk/react";
import { Leaf, Package, Gavel, Weight } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { LotCard } from "@/components/LotCard";
import { EsgPanel } from "@/components/EsgPanel";
import { TransactionsList } from "@/components/TransactionsList";
import { Countdown } from "@/components/Countdown";
import { CATEGORY_CO2 } from "@/lib/data";
import { clp, num, useApi } from "@/lib/api";

const Stat = ({ icon: Icon, label, value, testId }) => (
  <div className="bg-white border border-slate-200 rounded-2xl p-5">
    <p className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500"><Icon className="w-4 h-4 text-[#00b37a]" />{label}</p>
    <p className="font-display text-3xl font-bold text-[#05130e] mt-2" data-testid={testId}>{value}</p>
  </div>
);

const Empty = ({ text, to, cta, testId }) => (
  <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-slate-300" data-testid={testId}>
    <p className="text-slate-600 mb-4">{text}</p>
    <Button asChild className="rounded-full"><Link to={to}>{cta}</Link></Button>
  </div>
);

export default function Dashboard() {
  const api = useApi();
  const { user } = useUser();
  const [lots, setLots] = useState([]);
  const [bids, setBids] = useState([]);

  useEffect(() => {
    api("get", "/me/lots").then((r) => setLots(r.data)).catch(() => {});
    api("get", "/me/bids").then((r) => setBids(r.data)).catch(() => {});
  }, [api]);

  const active = lots.filter((l) => l.status === "active");
  const tons = active.reduce((s, l) => s + l.weight_tons, 0);
  const co2 = active.reduce((s, l) => s + l.weight_tons * (CATEGORY_CO2[l.category] || 1), 0);

  return (
    <main className="max-w-[1400px] mx-auto px-5 lg:px-8 py-12" data-testid="dashboard-page">
      <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#00b37a] mb-2">Mi panel</p>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#05130e]">Hola, {user?.firstName || "bienvenido"}</h1>
        <div className="flex flex-wrap gap-2">
          <Button asChild className="rounded-full"><Link to="/mis-empresas" data-testid="dashboard-companies-link">Mis empresas</Link></Button>
          <Button asChild variant="outline" className="rounded-full border-[#00c487] text-[#007a50]"><Link to="/mis-empresas#gri-306" data-testid="dashboard-gri-link">Informe GRI 306</Link></Button>
          <Button asChild variant="outline" className="rounded-full"><Link to="/perfil#documentos" data-testid="dashboard-enabling-docs-link">Documentos Habilitadores</Link></Button>
          <Button asChild variant="outline" className="rounded-full"><Link to="/perfil" data-testid="dashboard-profile-link">Verificación de gestor</Link></Button>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <Stat icon={Package} label="Lotes activos" value={active.length} testId="stat-lots" />
        <Stat icon={Weight} label="Toneladas ofertadas" value={num(tons, 1)} testId="stat-tons" />
        <Stat icon={Leaf} label="CO₂ eq. potencial (t)" value={num(co2, 1)} testId="stat-co2" />
        <Stat icon={Gavel} label="Mis ofertas" value={bids.length} testId="stat-bids" />
      </div>
      <Tabs defaultValue="lots">
        <TabsList className="mb-6 flex-wrap h-auto">
          <TabsTrigger value="lots" data-testid="tab-my-lots">Mis lotes</TabsTrigger>
          <TabsTrigger value="bids" data-testid="tab-my-bids">Mis ofertas</TabsTrigger>
          <TabsTrigger value="tx" data-testid="tab-transactions">Transacciones</TabsTrigger>
          <TabsTrigger value="esg" data-testid="tab-esg">Panel ESG</TabsTrigger>
        </TabsList>
        <TabsContent value="tx"><TransactionsList /></TabsContent>
        <TabsContent value="esg"><EsgPanel /></TabsContent>
        <TabsContent value="lots">
          {lots.length ? <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">{lots.map((l, i) => <LotCard key={l.id} lot={l} index={i} />)}</div>
            : <Empty text="Aún no publicas lotes." to="/publicar" cta="Publicar mi primer lote" testId="empty-my-lots" />}
        </TabsContent>
        <TabsContent value="bids">
          {bids.length ? (
            <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100" data-testid="my-bids-list">
              {bids.map((b) => (
                <Link key={b.id} to={`/lotes/${b.lot_id}`} className="flex flex-wrap items-center justify-between gap-3 p-4 hover:bg-[#f4f7f6] transition-colors">
                  <span className="font-medium text-[#05130e]">{b.lot_title}</span>
                  <span className="flex items-center gap-4 text-sm">
                    <span className="font-mono">{clp(b.amount)}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${b.leading ? "bg-[#00f2a1]/20 text-[#007a50]" : "bg-slate-100 text-slate-500"}`}>{b.leading ? "Liderando" : "Superada"}</span>
                    <Countdown endsAt={b.auction_ends_at} className="text-xs text-slate-500" testId={`my-bid-countdown-${b.id}`} />
                  </span>
                </Link>
              ))}
            </div>
          ) : <Empty text="Aún no ofertas en subastas." to="/marketplace?mode=subasta" cta="Ver subastas activas" testId="empty-my-bids" />}
        </TabsContent>
      </Tabs>
    </main>
  );
}
